/**
 * DynamicFooter — footer rendered from page_config.footer.
 * Renders nothing when footer.enabled is false.
 *
 * Two modes, chosen by the page author in the admin Header & Footer editor:
 *   'default' — the platform's Arcee-branded footer (ArceeDefaultFooter)
 *   'custom'  — the fully configurable layout built from this config object
 */

import React from 'react';
import ArceeDefaultFooter from './ArceeDefaultFooter';

const SOCIAL_ICON = {
  twitter:   'bi-twitter-x',
  facebook:  'bi-facebook',
  instagram: 'bi-instagram',
  linkedin:  'bi-linkedin',
  youtube:   'bi-youtube',
  github:    'bi-github',
  tiktok:    'bi-tiktok',
  discord:   'bi-discord',
  whatsapp:  'bi-whatsapp',
  email:     'bi-envelope-fill',
  phone:     'bi-telephone-fill',
  website:   'bi-globe2',
};

const SHADOW = {
  none: 'none',
  sm:   '0 -2px 8px rgba(0,0,0,0.12)',
  md:   '0 -4px 20px rgba(0,0,0,0.18)',
  lg:   '0 -8px 36px rgba(0,0,0,0.24)',
};

// Bootstrap column class based on total columns
const BS_COL = {
  1: 'col-12',
  2: 'col-12 col-sm-6',
  3: 'col-12 col-sm-6 col-md-4',
  4: 'col-12 col-sm-6 col-md-3',
};

function flexJustify(alignment) {
  return { left: 'flex-start', center: 'center', right: 'flex-end' }[alignment] || 'flex-start';
}

// Footers saved before the mode selector existed have no `mode` key. Anything
// the author actually configured must keep rendering as designed, so those fall
// back to 'custom'; an enabled-but-empty footer (which rendered as a bare strip)
// becomes 'default'.
export function resolveFooterMode(cfg) {
  if (!cfg) return 'default';
  if (cfg.mode === 'default' || cfg.mode === 'custom') return cfg.mode;
  const hasCustomContent =
    (cfg.columns || []).length > 0 ||
    (cfg.social_links || []).length > 0 ||
    (cfg.bottom_bar_links || []).length > 0 ||
    !!cfg.brand_name ||
    !!cfg.brand_logo_url ||
    !!cfg.tagline ||
    !!cfg.copyright_text;
  return hasCustomContent ? 'custom' : 'default';
}

export default function DynamicFooter({ footerCfg, theme }) {
  if (!footerCfg?.enabled) return null;

  if (resolveFooterMode(footerCfg) === 'default') {
    return <ArceeDefaultFooter theme={theme} />;
  }

  const {
    layout               = 'columns',
    content_alignment    = 'left',
    columns              = [],
    brand_name           = '',
    brand_logo_url       = '',
    tagline              = '',
    social_links         = [],
    social_icon_size     = 18,
    bottom_bar_enabled   = true,
    copyright_text       = '',
    bottom_bar_links     = [],
    bottom_bar_alignment = 'center',
    bg_color             = '#1e293b',
    text_color           = '#94a3b8',
    title_color          = '#ffffff',
    link_color           = '',
    bottom_bar_bg        = '',
    bottom_bar_text_color = '',
    font_size            = 14,
    title_font_size      = 13,
    font_weight          = '400',
    title_font_weight    = '700',
    padding_top          = 48,
    padding_bottom       = 32,
    column_gap           = 32,
    row_gap              = 10,
    min_height           = 0,
    border_top_enabled   = true,
    border_color         = 'rgba(255,255,255,0.1)',
    border_width         = 1,
    box_shadow           = 'none',
  } = footerCfg;

  const mobile_alignment = content_alignment;

  const linkCol    = link_color || theme?.primary || '#cbd5e1';
  const colCount   = Math.min(Math.max(columns.length, 1), 4);
  const hasCols    = layout === 'columns' && columns.length > 0;
  const hasBrand   = !hasCols && (brand_name || brand_logo_url || tagline);
  const hasSocial  = social_links.length > 0;
  const hasMain    = hasCols || hasBrand || hasSocial;
  const hasBottom  = bottom_bar_enabled && (copyright_text || bottom_bar_links.length > 0);

  const uid = `df-${Math.abs(bg_color.charCodeAt(1) || 0)}`;

  return (
    <footer style={{
      background:  bg_color,
      color:       text_color,
      fontSize:    `${font_size}px`,
      fontWeight:  font_weight,
      minHeight:   min_height > 0 ? `${min_height}px` : undefined,
      borderTop:   border_top_enabled ? `${border_width}px solid ${border_color}` : undefined,
      boxShadow:   SHADOW[box_shadow] || 'none',
    }}>

      {/* Responsive alignment override for mobile */}
      <style>{`
        @media (max-width: 575px) {
          .${uid}-col { text-align: ${mobile_alignment} !important; }
          .${uid}-col ul { justify-content: ${flexJustify(mobile_alignment)}; }
          .${uid}-col a { justify-content: ${flexJustify(mobile_alignment)}; }
        }
      `}</style>

      {/* ── Main content ── */}
      {hasMain && (
        <div style={{ padding: `${padding_top}px 0 ${padding_bottom}px` }}>
          <div className="container">

            {/* Multi-column layout */}
            {hasCols && (
              <div className="row" style={{ '--bs-gutter-x': `${column_gap}px`, rowGap: `${column_gap * 0.75}px` }}>
                {columns.map(col => (
                  <div key={col.id} className={`${BS_COL[colCount] || 'col-12'} ${uid}-col`}>

                    {col.title_visible !== false && col.title && (
                      <div style={{
                        color:          title_color,
                        fontSize:       `${title_font_size}px`,
                        fontWeight:     title_font_weight,
                        marginBottom:   14,
                        textTransform:  'uppercase',
                        letterSpacing:  '0.07em',
                        textAlign:      content_alignment,
                      }}>
                        {col.title}
                      </div>
                    )}

                    <ul style={{
                      listStyle: 'none',
                      padding: 0,
                      margin: 0,
                      textAlign: content_alignment,
                    }}>
                      {(col.items || []).map(item => (
                        <li key={item.id} style={{ marginBottom: `${row_gap + 2}px` }}>
                          {item.type === 'link' ? (
                            <a
                              href={item.url || '#'}
                              target={item.open_new_tab ? '_blank' : undefined}
                              rel={item.open_new_tab ? 'noopener noreferrer' : undefined}
                              style={{
                                color: linkCol,
                                textDecoration: 'none',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                transition: 'opacity 0.15s',
                              }}
                              onMouseEnter={e => (e.currentTarget.style.opacity = '0.65')}
                              onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
                            >
                              {item.icon && <i className={`bi ${item.icon}`} style={{ fontSize: `${font_size}px` }} />}
                              {item.label}
                            </a>
                          ) : (
                            <span style={{ color: text_color, lineHeight: 1.6 }}>
                              {item.text || item.label}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            {/* Centered / brand block */}
            {hasBrand && (
              <div style={{ textAlign: 'center', marginBottom: hasSocial ? 28 : 0 }}>
                {brand_logo_url && (
                  <img
                    src={brand_logo_url}
                    alt={brand_name || 'Logo'}
                    style={{ maxHeight: 56, maxWidth: 200, objectFit: 'contain', marginBottom: 14 }}
                    onError={e => { e.currentTarget.style.display = 'none'; }}
                  />
                )}
                {brand_name && (
                  <div style={{ color: title_color, fontSize: `${title_font_size + 4}px`, fontWeight: 700, marginBottom: 6 }}>
                    {brand_name}
                  </div>
                )}
                {tagline && (
                  <div style={{ opacity: 0.72, maxWidth: 520, margin: '0 auto', lineHeight: 1.6 }}>
                    {tagline}
                  </div>
                )}
              </div>
            )}

            {/* Social icons */}
            {hasSocial && (
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 10,
                justifyContent: flexJustify(content_alignment),
                marginTop: (hasCols || hasBrand) ? 28 : 0,
              }}>
                {social_links.map((s, i) => (
                  <a
                    key={i}
                    href={s.url || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={s.label || s.platform}
                    style={{
                      color: linkCol,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width:  social_icon_size + 14,
                      height: social_icon_size + 14,
                      borderRadius: '50%',
                      border: `1.5px solid ${linkCol}55`,
                      textDecoration: 'none',
                      transition: 'all 0.15s',
                      flexShrink: 0,
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = `${linkCol}22`; e.currentTarget.style.borderColor = linkCol; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = `${linkCol}55`; }}
                  >
                    <i className={`bi ${SOCIAL_ICON[s.platform] || 'bi-link-45deg'}`} style={{ fontSize: social_icon_size }} />
                  </a>
                ))}
              </div>
            )}

          </div>
        </div>
      )}

      {/* ── Bottom bar ── */}
      {hasBottom && (
        <div style={{
          background: bottom_bar_bg || 'rgba(0,0,0,0.18)',
          borderTop:  `1px solid ${border_color}`,
          padding:    '12px 0',
        }}>
          <div className="container" style={{
            display:        'flex',
            flexWrap:       'wrap',
            alignItems:     'center',
            gap:            '6px 20px',
            justifyContent: flexJustify(bottom_bar_alignment),
          }}>
            {copyright_text && (
              <span style={{
                fontSize: `${Math.max(font_size - 2, 11)}px`,
                color:    bottom_bar_text_color || text_color,
                opacity:  0.7,
              }}>
                {copyright_text}
              </span>
            )}
            {bottom_bar_links.map((link, i) => (
              <a
                key={i}
                href={link.url || '#'}
                style={{
                  color: linkCol,
                  textDecoration: 'none',
                  fontSize: `${Math.max(font_size - 2, 11)}px`,
                  opacity: 0.78,
                  transition: 'opacity 0.15s',
                }}
                onMouseEnter={e => (e.currentTarget.style.opacity = '1')}
                onMouseLeave={e => (e.currentTarget.style.opacity = '0.78')}
              >
                {link.label}
              </a>
            ))}
          </div>
        </div>
      )}

    </footer>
  );
}
